'use client';

import { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/shadcn/components/ui/dialog';
import { XIcon, SearchIcon, Euro, Ruler } from 'lucide-react';
import { cn } from '@/shadcn/lib/utils';
import { MultiSelectPopover } from '@/shared/components/ui/MultiSelectPopover';
import {
    BadgeSuccessIcon,
    HouseOutlinedIcon,
    NewBuildingIcon,
    Survey3DIcon,
    YoutubeIcon,
    LocationOutlinedIcon,
} from '@/shared/components/icons';
import { CHIP_BASE, CHIP_ON, CHIP_OFF } from '@/shared/components/ui/chip';
import {
    ListingPropertySubtype,
    LISTING_PROPERTY_SUBTYPE_LABELS,
} from '../../listings/definitions/propertyTypes';
import {
    PRICE_STEP,
    PRICE_FALLBACK_MAX,
    PRICE_QUICK_RANGES,
    AREA_STEP,
    AREA_FALLBACK_MAX,
    AREA_QUICK_RANGES,
    formatPrice,
    formatArea,
} from '../constants/filterConstants';
import type { ListingsClassicFilterState } from '../../listings/hooks/useListingsClassicFilter';
import { ListingsFilterSettings } from '../../listings/types';
import { Button } from '@/shared/components/ui/Button';
import { Button as ShadcnButton } from '@/shadcn/components/ui/button';
import { TxnTypeOptions } from './fields/TxnTypeOptions';
import { PropertyTypeAccordion } from './fields/PropertyTypeAccordion';
import { RangeField } from './fields/RangeField';
import { getListings } from '@/api/generated/listings';
import { LocationOptions } from '@/features/locations/components/LocationOptions';

interface Props {
    open?: boolean;
    onClose?: () => void;
    filterState?: ListingsClassicFilterState;
    filterSettings?: ListingsFilterSettings;
}

/* Prepinace ako chips - rovnaky jazyk ako typy nehnutelnosti. */
const TOGGLES = [
    { field: 'newBuildsOnly' as const, icon: NewBuildingIcon, label: 'Len novostavby' },
    { field: 'withVideo' as const, icon: YoutubeIcon, label: 'S videoprehliadkou' },
    { field: 'with3dTour' as const, icon: Survey3DIcon, label: 'S 3D prehliadkou' },
];

/** Sekcia s nadpisom - jednotny vertikalny rytmus dialogu. */
function Section(props: { title: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={cn('relative min-w-0', props.className)}>
            <p className="text-[13px] font-[600] mb-[8px] pl-[13px] xl:text-[14px]">
                {props.title}
            </p>
            {props.children}
        </div>
    );
}

export function AdvertsClassicAdvancedFilter({
    open,
    onClose,
    filterState,
    filterSettings,
}: Props) {
    /* Pocet vysledkov pre tlacidlo Vyhladat - nacita sa len ked je dialog otvoreny. */
    const [count, setCount] = useState<number | null>(null);
    const reqRef = useRef(0);

    const formikValues = filterState?.formik.values;
    const priceFromRaw = filterState?.priceFrom;
    const priceToRaw = filterState?.priceTo;
    const areaFromRaw = filterState?.areaFrom;
    const areaToRaw = filterState?.areaTo;

    useEffect(() => {
        if (!open || !filterState) {
            setCount(null);
            return;
        }
        const id = ++reqRef.current;
        const timer = setTimeout(async () => {
            try {
                const res = await getListings(
                    { pagination: { limit: 1 }, filter: filterState.buildFilterParams() },
                    { portalApiKey: true },
                );
                // ignoruj odpoved zo starsieho requestu
                if (id !== reqRef.current) return;
                const total = (res as any)?.metadata?.pagination?.total;
                setCount(typeof total === 'number' ? total : null);
            } catch {
                if (id === reqRef.current) setCount(null);
            }
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open,
        formikValues,
        priceFromRaw,
        priceToRaw,
        areaFromRaw,
        areaToRaw,
    ]);

    if (!filterState || !filterSettings) return null;

    const {
        formik,
        displayLocations,
        startedSearchLocations,
        startSearchLocation,
        priceFrom,
        setPriceFrom,
        priceTo,
        setPriceTo,
        areaFrom,
        setAreaFrom,
        areaTo,
        setAreaTo,
        hasActiveFilters,
        resetFilters,
    } = filterState;

    const priceMin = filterSettings.minPrice ?? 0;
    const priceMax = filterSettings.maxPrice ?? PRICE_FALLBACK_MAX;
    const areaMin = filterSettings.minArea ?? 0;
    const areaMax = filterSettings.maxArea ?? AREA_FALLBACK_MAX;

    /* Vyhladat ide cez formik, aby prebehla validacia - inak by sa dalo
       navigovat na /inzeraty/undefined/ bez zvoleneho typu inzeratu. */
    async function onSearch() {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            formik.setTouched({ locations: true, txnType: true });
            formik.setErrors(errors);
            return;
        }
        formik.submitForm();
        onClose?.();
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                showCloseButton={false}
                className="p-0 border-0 gap-0 sm:max-w-[800px] max-h-[90dvh] overflow-hidden flex flex-col z-[10000]"
            >
                <DialogHeader className="shrink-0">
                    <div
                        className="flex justify-between w-full items-center bg-cust-primary p-[20px] rounded-t-[15px] bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: "url('/images/filter/advanced_filter_bgr.jpg')",
                        }}
                    >
                        <div className="size-[35px]" />
                        <DialogTitle className="text-[18px] font-[700]">
                            Rozšírené filtre
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Podrobné nastavenie vyhľadávania nehnuteľností
                        </DialogDescription>
                        <ShadcnButton
                            aria-label="Zavrieť"
                            className="rounded-full bg-white/20 shadow-[0_2px_10px_rgba(0,0,0,0.08)] p-0 size-[35px] text-black cursor-pointer hover:bg-white/40"
                            onClick={() => onClose?.()}
                        >
                            <XIcon className="text-[15px]" />
                        </ShadcnButton>
                    </div>
                </DialogHeader>

                {/* telo je jediny scroll kontajner - hlavicka a paticka ostavaju dostupne */}
                <div className="flex-1 min-h-0 overflow-y-auto vox-scroll-light bg-white p-[20px] flex flex-col gap-[22px] md:p-[35px] md:gap-[26px]">
                    {/* Location */}
                    <Section title="Kde?" className="z-[3]">
                        <MultiSelectPopover
                            type="multi"
                            placeholder="Vyberte miesto"
                            items={formik.values.locations.map((loc) => ({
                                id: loc.slug,
                                value: loc.name,
                            }))}
                            iconLeft={
                                <LocationOutlinedIcon className="size-[16px] text-black/80" />
                            }
                            onChangeQuery={(value) => startSearchLocation(value)}
                            onDeleteItem={(item) => {
                                formik.setFieldValue(
                                    'locations',
                                    formik.values.locations.filter((i) => i.slug !== item.id),
                                );
                            }}
                        >
                            <LocationOptions
                                value={formik.values.locations}
                                onChange={(next) => formik.setFieldValue('locations', next)}
                                displayLocations={displayLocations}
                                startedSearchLocations={startedSearchLocations}
                            />
                        </MultiSelectPopover>
                        {(formik.submitCount > 0 || formik.touched.locations) &&
                            formik.errors.locations && (
                                <p className="ml-[5px] mt-[5px] text-[11px] text-red-500">
                                    {formik.errors.locations as string}
                                </p>
                            )}
                    </Section>

                    {/* Transaction type */}
                    <Section title="Typ inzerátu:" className="z-[2]">
                        <MultiSelectPopover
                            type="classic"
                            placeholder="Zvoľte typ inzerátu"
                            items={
                                formik.values.txnType
                                    ? [
                                          {
                                              id: formik.values.txnType.slug,
                                              value: formik.values.txnType.value,
                                          },
                                      ]
                                    : []
                            }
                            iconLeft={<BadgeSuccessIcon className="size-[18px] text-black/80" />}
                        >
                            <TxnTypeOptions
                                value={formik.values.txnType}
                                onChange={(type) => formik.setFieldValue('txnType', type)}
                            />
                        </MultiSelectPopover>
                        {(formik.submitCount > 0 || formik.touched.txnType) &&
                            formik.errors.txnType && (
                                <p className="ml-[5px] mt-[5px] text-[11px] text-red-500">
                                    {formik.errors.txnType as string}
                                </p>
                            )}
                    </Section>

                    {/* Property type */}
                    <Section title="Nehnuteľnosť:" className="z-[1]">
                        <MultiSelectPopover
                            type="multi"
                            placeholder="Vyberte nehnuteľnosť"
                            items={formik.values.propertyTypes
                                .filter((t): t is Exclude<typeof t, null> => t !== null)
                                .map((type) => ({
                                    id: type,
                                    value: LISTING_PROPERTY_SUBTYPE_LABELS[type],
                                }))}
                            iconLeft={<HouseOutlinedIcon className="size-[15px] text-black/80" />}
                            onDeleteItem={(item) => {
                                formik.setFieldValue(
                                    'propertyTypes',
                                    formik.values.propertyTypes.filter((i) => i !== item.id),
                                );
                            }}
                        >
                            <PropertyTypeAccordion
                                value={formik.values.propertyTypes}
                                onChange={(next) => formik.setFieldValue('propertyTypes', next)}
                            />
                        </MultiSelectPopover>
                    </Section>

                    {/* Toggles ako chips */}
                    <div className="flex flex-wrap gap-[8px]">
                        {TOGGLES.map(({ field, icon: Icon, label }) => {
                            const on = formik.values[field];
                            return (
                                <button
                                    key={field}
                                    type="button"
                                    aria-pressed={on}
                                    className={cn(
                                        CHIP_BASE,
                                        on ? CHIP_ON : CHIP_OFF,
                                        'flex items-center gap-[8px] py-[9px] px-[14px] text-[13px]',
                                    )}
                                    onClick={() => formik.setFieldValue(field, !on)}
                                >
                                    <Icon className="size-[16px]" />
                                    {label}
                                    {/* zapnuty prepinac ukaze x - jasne, ze sa da vypnut.
                                        Klik kdekolvek po chipe ho aj tak prepne. */}
                                    <XIcon
                                        className={cn(
                                            'size-[13px] shrink-0 -mr-[3px]',
                                            'transition duration-200 ease-[var(--ease-rx)]',
                                            on ? 'opacity-60' : 'w-0 opacity-0',
                                        )}
                                    />
                                </button>
                            );
                        })}
                    </div>

                    {/* Price - z-0 drzi uchyty slidera (z-2) vo vlastnom stacking
                        kontexte, inak by prebijali rozbaleny pulldown nad nimi */}
                    <Section title="Cena" className="z-0">
                        <RangeField
                            min={priceMin}
                            max={priceMax}
                            step={PRICE_STEP}
                            from={priceFrom ?? priceMin}
                            to={priceTo ?? priceMax}
                            format={formatPrice}
                            quickRanges={PRICE_QUICK_RANGES}
                            withInputs
                            inputIcon={<Euro className="size-[15px] text-black/80" />}
                            labelFrom="Cena od"
                            labelTo="Cena do"
                            onChange={(from, to) => {
                                setPriceFrom(from);
                                setPriceTo(to);
                            }}
                        />
                    </Section>

                    {/* Area */}
                    <Section title="Plocha" className="z-0">
                        <RangeField
                            min={areaMin}
                            max={areaMax}
                            step={AREA_STEP}
                            from={areaFrom ?? areaMin}
                            to={areaTo ?? areaMax}
                            format={formatArea}
                            quickRanges={AREA_QUICK_RANGES}
                            withInputs
                            inputIcon={<Ruler className="size-[15px] text-black/80" />}
                            labelFrom="Plocha od"
                            labelTo="Plocha do"
                            onChange={(from, to) => {
                                setAreaFrom(from);
                                setAreaTo(to);
                            }}
                        />
                    </Section>
                </div>

                {/* Sticky paticka - akcie ostavaju dostupne aj pri scrollovani */}
                <div className="shrink-0 flex justify-between items-center gap-[10px] bg-white border-t border-black/[.06] p-[20px] rounded-b-[15px] md:px-[35px]">
                    <button
                        type="button"
                        disabled={!hasActiveFilters}
                        className={cn(
                            'text-[13px] font-[600] py-[10px] px-[16px] rounded-full cursor-pointer',
                            'text-black/60 hover:bg-[#f6f5f0] hover:text-black',
                            'transition duration-200 ease-[var(--ease-rx)]',
                            'disabled:opacity-40 disabled:pointer-events-none',
                        )}
                        onClick={resetFilters}
                    >
                        Vymazať filtre
                    </button>
                    <Button color="black" onClick={onSearch}>
                        <SearchIcon className="size-[18px] text-[#fff]" />
                        <p className="text-[13px]">
                            Vyhľadať
                            {count !== null && (
                                <span className="opacity-70"> ({count})</span>
                            )}
                        </p>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
